import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });

  failedQueue = [];
};

// Configurar interceptor para refresh automático
export const setupAxiosInterceptors = (onLogout: () => void) => {
  console.log('[Axios Interceptor] Configurando interceptores...');
  
  // Request interceptor - agregar token a cada petición
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('[Request Interceptor] No se encontró token para:', config.url);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - manejar errores 401
  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

      console.log('[Response Interceptor] Error capturado:', {
        status: error.response?.status,
        url: originalRequest?.url,
        retry: originalRequest?._retry
      });

      // Si es 401 y no es login/refresh ni perfil (contraseña actual errónea no debe disparar refresh ni cerrar sesión)
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes('/auth/login') &&
        !originalRequest.url?.includes('/auth/refresh') &&
        !originalRequest.url?.includes('/auth/profile')
      ) {
        console.log('[Response Interceptor] Error 401 detectado, intentando refresh...');
        
        if (isRefreshing) {
          console.log('[Response Interceptor] Ya hay un refresh en proceso, poniendo en cola...');
          // Si ya se está refrescando, poner esta petición en cola
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return axios(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          // No hay refresh token, hacer logout
          console.warn('[Response Interceptor] No hay refresh token, cerrando sesión...');
          isRefreshing = false;
          onLogout();
          return Promise.reject(error);
        }

        try {
          console.log('[Response Interceptor] Intentando refrescar token...');
          // Intentar refrescar el token
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refreshToken,
          });

          const refreshPayload = response.data?.data || response.data;
          const {
            accessToken,
            refreshToken: newRefreshToken,
            allowedModules,
            allowedActions
          } = refreshPayload;

          if (!accessToken) {
            throw new Error('No access token in refresh response');
          }

          console.log('[Response Interceptor] Token refrescado exitosamente');

          if (Array.isArray(allowedModules) || Array.isArray(allowedActions)) {
            window.dispatchEvent(
              new CustomEvent('vms:rbac-update', {
                detail: {
                  ...(Array.isArray(allowedModules) ? { allowedModules } : {}),
                  ...(Array.isArray(allowedActions) ? { allowedActions } : {})
                }
              })
            );
            try {
              const rawUser = localStorage.getItem('user');
              if (rawUser && rawUser !== 'undefined' && rawUser !== 'null') {
                const u = JSON.parse(rawUser);
                if (Array.isArray(allowedModules)) u.allowedModules = allowedModules;
                if (Array.isArray(allowedActions)) u.allowedActions = allowedActions;
                localStorage.setItem('user', JSON.stringify(u));
              }
            } catch {
              /* ignore */
            }
          }

          // Guardar nuevos tokens
          localStorage.setItem('token', accessToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // Actualizar header por defecto de axios
          axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          // Procesar cola de peticiones pendientes
          processQueue(null, accessToken);

          // Reintentar la petición original
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return axios(originalRequest);
        } catch (refreshError) {
          // Refresh falló, hacer logout
          console.error('[Response Interceptor] Refresh falló:', refreshError);
          processQueue(refreshError, null);
          onLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Tras refresh, el reintento puede seguir devolviendo 401 (token inválido, etc.);
      // sin esto _retry=true hace que se omita el bloque anterior y no se llama a logout.
      if (error.response?.status === 401 && originalRequest?._retry) {
        console.warn('[Response Interceptor] 401 tras reintentar; cerrando sesión');
        onLogout();
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );
};

/** Rutas donde no se debe cerrar sesión por inactividad (monitoreo: cámaras en vivo, grabaciones, mapas) */
const MONITORING_ROUTES = ['/streams', '/recordings', '/download-certificates', '/map'];

/** Comprueba si la ruta actual es de solo monitoreo (no cerrar sesión por inactividad) */
export const isMonitoringRoute = (): boolean => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return MONITORING_ROUTES.some((route) => path === route || path.startsWith(route + '/'));
};

// Hook para detectar actividad del usuario
export const setupActivityDetection = (
  onInactive: () => void,
  timeoutMinutes: number = 30,
  shouldSkipLogout?: () => boolean
) => {
  let inactivityTimer: NodeJS.Timeout;

  const resetTimer = () => {
    clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(() => {
      if (shouldSkipLogout?.()) {
        console.log('Usuario en pantalla de monitoreo (Stream/Grabaciones/Mapas), no se cierra sesión por inactividad.');
        resetTimer();
        return;
      }
      console.log('Usuario inactivo, cerrando sesión...');
      onInactive();
    }, timeoutMinutes * 60 * 1000);
  };

  // Eventos que indican actividad del usuario
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

  events.forEach(event => {
    document.addEventListener(event, resetTimer, true);
  });

  // Iniciar el timer
  resetTimer();

  // Función de limpieza
  return () => {
    clearTimeout(inactivityTimer);
    events.forEach(event => {
      document.removeEventListener(event, resetTimer, true);
    });
  };
};
